/* eslint-disable react-hooks/exhaustive-deps */
import { useTheme } from "@emotion/react";
import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../provider/authProvider";
import LoanService from "../../services/loanService";
import BranchDropdown from "../../components/dropdowns/branchDropdown";
import CustomInput from "../../components/inputs/customInput";
import DateInput from "../../components/inputs/dateInput";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import PageDropdown from "../../components/dropdowns/pageDropdown";
import DriverDropdown from "../../components/dropdowns/driverDropdown";
import TransferTypeDropdown from "../../components/dropdowns/transferTypeDropdown";
import Popup from "../../components/popups/popup";
import { useNavigate, useParams } from "react-router-dom";


const UpdateLoan = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const { id } = useParams();
    const [loan, setLoan] = useState({});

    const { user } = useContext(AuthContext);
    const userPermissions = user?.permissions || [];
    const allBranchPermission = userPermissions?.some(p => p.value === 'allBranches');

    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedPage, setSelectedPage] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());

    const [updateErrors, setUpdateErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isModalLoading, setIsModalLoading] = useState(true);

    const [formValues, setFormValues] = useState({
        amount: loan.amount,
        type: loan.type,
        userType: loan.userType,
        branchId: loan.branchId,
        driverId: loan.driverId,
        pageId: loan.pageId,
        note: loan.note,
    });

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const numericValue = value.replace(/,/g, "");

        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: numericValue,
        }));
    };

    const validateForm = () => {
        const newUpdateErrors = {};

        if (isNaN(formValues.amount)) {
            newUpdateErrors.amount = t('amountNumber');
        }

        setUpdateErrors(newUpdateErrors);

        return Object.keys(newUpdateErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const { userType, ...formInfo } = formValues;

                const data = await LoanService.update({ id: loan.id, ...formInfo, date: selectedDate });
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);
                    setSelectedBranch(null);
                    setSelectedType('');
                    setSelectedDriver(null);
                    setSelectedPage(null);
                    setSelectedDate(dayjs());
                    navigate('/loans');
                }

                setUpdateErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setUpdateErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setUpdateErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const fetchLoan = async () => {
        try {
            const data = await LoanService.fetchOne(id);

            setLoan(data);
            setIsModalLoading(false);

            console.log(data);

        } catch (error) {
            console.error('Error fetching laon:', error);
        }
    };

    useEffect(() => {
        if (loan) {
            setFormValues({
                amount: loan.amount || '',
                type: loan.type || '',
                userType: loan.userType || '',
                branchId: loan.branchId || '',
                driverId: loan.driverId || '',
                pageId: loan.pageId || '',
                note: loan.note || '',
            });
            setSelectedBranch(loan.branch || null);
            setSelectedDriver(loan.driver || null);
            setSelectedPage(loan.page || null);
            setSelectedType((loan.type === 'withdraw' ? t('takeMoney') : t('giveMoney')) || null);


            const parsedDate = dayjs(loan.date, "DD/MM/YYYY");
            if (parsedDate.isValid()) {
                setSelectedDate(parsedDate);
            } else {
                console.error("Invalid date format");
            }

            setUpdateErrors({});
        }
    }, [loan]);


    useEffect(() => {
        setIsModalLoading(true);
        fetchLoan();
    }, []);

    return (
        <Box sx={{
            p: isTablet ? 0.6 : 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    px: isTablet ? 1 : 4,
                    py: 1
                }}
            >

                {
                    isModalLoading ? <Box sx={{ justifyContent: 'center', display: 'flex', my: 10 }} >
                        <CircularProgress color="primary" size={28} /> </Box>
                        : <Box>
                            {
                                allBranchPermission &&
                                <BranchDropdown
                                    helperText={updateErrors.branch}
                                    error={!!updateErrors.branch}
                                    setFormValues={setFormValues}
                                    isFilter={false}
                                    selectedBranch={selectedBranch}
                                    setSelectedBranch={setSelectedBranch}
                                />
                            }

                            <Box sx={{
                                display: 'flex',
                                flexDirection: isTablet ? 'column' : 'row',
                                justifyContent: 'space-between'
                            }}>
                                <Box sx={{
                                    width: isTablet ? '100%' : '48%'
                                }}>
                                    {
                                        loan.userType === 'page' ?
                                            <PageDropdown
                                                helperText={updateErrors.page}
                                                error={!!updateErrors.page}
                                                setFormValues={setFormValues}
                                                isFilter={false}
                                                selectedPage={selectedPage}
                                                setSelectedPage={setSelectedPage}
                                            /> :
                                            <DriverDropdown
                                                helperText={updateErrors.driver}
                                                error={!!updateErrors.driver}
                                                setFormValues={setFormValues}
                                                isFilter={false}
                                                selectedDriver={selectedDriver}
                                                setSelectedDriver={setSelectedDriver}
                                            />
                                    }
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: isTablet ? 'column' : 'row', justifyContent: 'space-between' }}>
                                <Box sx={{ width: isTablet ? '100%' : '48%' }}>
                                    <TransferTypeDropdown
                                        helperText={updateErrors.type}
                                        error={!!updateErrors.type}
                                        setFormValues={setFormValues}
                                        isFilter={false}
                                        selectedType={selectedType}
                                        setSelectedType={setSelectedType}
                                        changeLangForLoan={true}
                                    />
                                </Box>

                                <Box sx={{ width: isTablet ? '100%' : '48%' }}>
                                    <CustomInput
                                        label={t('amount')}
                                        placeholder={t('amountHere')}
                                        name="amount"
                                        type='text'
                                        value={formValues.amount ? Number(formValues.amount).toLocaleString() : 0}
                                        error={!!updateErrors.amount}
                                        helperText={updateErrors.amount}
                                        onChange={handleNumberChange}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: isTablet ? 'column' : 'row', justifyContent: 'space-between' }}>
                                <Box sx={{ width: isTablet ? '100%' : '48%' }}>
                                    <DateInput
                                        label={t('date')}
                                        selectedDate={selectedDate}
                                        setSelectedDate={setSelectedDate}
                                        handleDateChange={handleDateChange}
                                    />
                                </Box>

                                <Box sx={{ width: isTablet ? '100%' : '48%', height: 100 }}>
                                    <CustomInput
                                        label={t('note')}
                                        placeholder={t('noteHere')}
                                        name="note"
                                        type='text'
                                        value={formValues.note}
                                        onChange={handleChange}
                                    />
                                </Box>
                            </Box>

                            {updateErrors.general && (
                                <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                                    {updateErrors.general}
                                </Typography>
                            )}

                            <Box sx={{
                                display: 'flex', justifyContent: 'center', alignItems: 'center',
                                mt: updateErrors.general ? 1 : 4,
                                mb: 2
                            }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    sx={{ textTransform: 'none', width: 140, height: 40 }}
                                >
                                    {isLoading ? <CircularProgress color="secondary" size={28} /> :
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <p>{t('update')}</p>
                                            <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                                        </Box>
                                    }
                                </Button>
                            </Box>
                        </Box>
                }
            </Box>
        </Box>
    );
}

export default UpdateLoan;